import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Eye, EyeOff, Sun, Moon, Loader2, Mail, Lock, X } from 'lucide-react';

const LOGO_URL = '/estrapat-logo.png';
const TERMS_VERSION = 'v1';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline styles para HTML generado dinámicamente (Tailwind no aplica a dangerouslySetInnerHTML)
const MD_STYLES = {
  h2: 'font-size:1.0625rem;font-weight:700;color:#ffffff;margin-top:1rem;margin-bottom:0.4rem;',
  h3: 'font-size:0.9375rem;font-weight:600;color:#93c5fd;margin-top:0.75rem;margin-bottom:0.25rem;',
  p:  'font-size:0.875rem;color:#d1d5db;line-height:1.625;margin-bottom:0.5rem;',
  li: 'font-size:0.875rem;color:#d1d5db;line-height:1.6;margin-bottom:0.125rem;',
  ul: 'padding-left:1.25rem;list-style-type:disc;margin-bottom:0.5rem;',
  table: 'font-size:0.8125rem;color:#d1d5db;border-collapse:collapse;width:100%;margin-bottom:0.75rem;',
  td: 'border:1px solid #374151;padding:0.3rem 0.5rem;',
  hr: 'border:none;border-top:1px solid #374151;margin:0.75rem 0;',
  strong: 'font-weight:700;color:#e5e7eb;',
};

function inlineFmt(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, `<strong style="${MD_STYLES.strong}">$1</strong>`)
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function markdownToHtml(md) {
  const lines = md.split('\n');
  const parts = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table separator row — skip
    if (/^\|[\s\-:|]+\|/.test(line)) { i++; continue; }

    // Table rows
    if (line.startsWith('|')) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!/^\|[\s\-:|]+\|/.test(lines[i])) {
          const cells = lines[i].split('|').slice(1, -1).map(c => c.trim());
          rows.push(`<tr>${cells.map(c => `<td style="${MD_STYLES.td}">${inlineFmt(c)}</td>`).join('')}</tr>`);
        }
        i++;
      }
      if (rows.length) parts.push(`<table style="${MD_STYLES.table}"><tbody>${rows.join('')}</tbody></table>`);
      continue;
    }

    // HR
    if (line.trim() === '---') {
      parts.push(`<hr style="${MD_STYLES.hr}"/>`);
      i++; continue;
    }

    // ### Heading (before ## check)
    if (line.startsWith('### ')) {
      parts.push(`<h3 style="${MD_STYLES.h3}">${inlineFmt(line.slice(4))}</h3>`);
      i++; continue;
    }

    // ## Heading (before # check)
    if (line.startsWith('## ')) {
      parts.push(`<h2 style="${MD_STYLES.h2}">${inlineFmt(line.slice(3))}</h2>`);
      i++; continue;
    }

    // # Heading
    if (line.startsWith('# ')) {
      parts.push(`<h2 style="${MD_STYLES.h2}">${inlineFmt(line.slice(2))}</h2>`);
      i++; continue;
    }

    // List items
    if (/^[\-\*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[\-\*] /.test(lines[i])) {
        items.push(`<li style="${MD_STYLES.li}">${inlineFmt(lines[i].slice(2))}</li>`);
        i++;
      }
      parts.push(`<ul style="${MD_STYLES.ul}">${items.join('')}</ul>`);
      continue;
    }

    // Empty line
    if (line.trim() === '') { i++; continue; }

    // Paragraph — collect until blank line or block-level marker
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('|') &&
      !/^[\-\*] /.test(lines[i]) &&
      lines[i].trim() !== '---'
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) parts.push(`<p style="${MD_STYLES.p}">${inlineFmt(paraLines.join(' '))}</p>`);
  }

  return parts.join('');
}

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, sessionExpired, setSessionExpired, logoutReason } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    setSessionExpired(false);
  }, [setSessionExpired]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  // true when terms were auto-filled from BD (no need to call accept-terms again)
  const [autoAccepted, setAutoAccepted] = useState(false);

  const [legalModal, setLegalModal] = useState({ open: false, title: '' });
  const [legalHtml, setLegalHtml] = useState('');

  const openLegal = async (title, url) => {
    setLegalHtml('');
    setLegalModal({ open: true, title });
    try {
      const res = await fetch(url);
      const md = await res.text();
      setLegalHtml(markdownToHtml(md));
    } catch {
      setLegalHtml(`<p style="${MD_STYLES.p}">No se pudo cargar el documento.</p>`);
    }
  };

  const closeLegal = () => setLegalModal({ open: false, title: '' });

  const handleEmailChange = (e) => {
    setLoginData({ ...loginData, email: e.target.value });
    // Reset auto-flag when user edits the email field
    setAutoAccepted(false);
  };

  const handleEmailBlur = async () => {
    const email = loginData.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      const res = await fetch(`${API}/auth/terms-status?email=${encodeURIComponent(email)}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.accepted) {
        setAcceptTerms(true);
        setAcceptPrivacy(true);
        setAutoAccepted(true);
      }
    } catch {
      // silently fail — user can still accept manually
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Persist acceptance to BD only when the user checked boxes in this session
    if (!autoAccepted) {
      try {
        await fetch(`${API}/auth/accept-terms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: loginData.email.toLowerCase().trim(),
            terms_version: TERMS_VERSION,
          }),
        });
      } catch {
        // non-fatal — proceed with login
      }
    }

    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('¡Bienvenido!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex relative"
      style={{
        backgroundImage: "url('/Login_page_4.png'), linear-gradient(135deg, #0a1940, #0f2d6b, #0a3d8f)",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <style>{`
        @keyframes borderGlow {
          0%, 100% { box-shadow: 4px 0 40px rgba(30,100,255,0.12); }
          50%       { box-shadow: 4px 0 60px rgba(30,100,255,0.22); }
        }
        .login-input::placeholder { color: rgba(255,255,255,0.28); }
      `}</style>

      {/* Modal legal — glassmorphism, z sobre todo */}
      {legalModal.open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={closeLegal}
        >
          <div
            style={{
              background: 'rgba(8,20,60,0.88)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(100,160,255,0.25)',
              borderRadius: '0.75rem',
              width: '100%',
              maxWidth: '42rem',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 48px rgba(0,0,0,0.55)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabecera */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '1rem 1.25rem',
              borderBottom: '1px solid rgba(100,160,255,0.15)',
              flexShrink: 0,
            }}>
              <h2 style={{ color: '#ffffff', fontSize: '1rem', fontWeight: 700, margin: 0 }}>
                {legalModal.title}
              </h2>
              <button
                onClick={closeLegal}
                aria-label="Cerrar"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.45)', padding: '0.25rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '0.375rem', transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
              >
                <X size={18} />
              </button>
            </div>

            {/* Área de scroll con contenido renderizado */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
              {legalHtml
                ? <div dangerouslySetInnerHTML={{ __html: legalHtml }} />
                : (
                  <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.875rem', fontStyle: 'italic' }}>
                    Cargando documento…
                  </p>
                )
              }
            </div>

            {/* Pie con botón Cerrar */}
            <div style={{
              padding: '0.875rem 1.25rem',
              borderTop: '1px solid rgba(100,160,255,0.15)',
              display: 'flex', justifyContent: 'flex-end',
              flexShrink: 0,
            }}>
              <button
                onClick={closeLegal}
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(100,160,255,0.3)',
                  borderRadius: '0.5rem',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  padding: '0.5rem 1.25rem',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dark mode toggle — absoluto sobre el fondo */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
        data-testid="login-theme-toggle"
      >
        {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>

      {/* Panel izquierdo — glassmorphism oscuro */}
      <div
        className="w-full md:w-[38%] lg:w-[28%] min-h-screen flex flex-col justify-between px-10 py-12"
        style={{
          background: 'rgba(8,20,60,0.55)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRight: '1px solid rgba(100,160,255,0.2)',
          animation: 'borderGlow 4s ease-in-out infinite',
        }}
      >
        {/* Bloque central con form */}
        <div className="flex-1 flex flex-col justify-center space-y-7">

          {/* Logo + títulos */}
          <div className="flex flex-col items-center space-y-3">
            <img
              src={LOGO_URL}
              alt="EstraPat"
              className="w-64"
              style={{ filter: 'drop-shadow(0 0 16px rgba(59,130,246,0.45))' }}
            />
            <div className="text-center">
              <p className="text-center text-white font-bold text-4xl tracking-wide mt-1">
                EstraPat CRM
              </p>
              <p className="text-center text-blue-300 text-sm tracking-widest mb-2">
                Estrategias Patrimoniales Inteligentes
              </p>
              <h2 className="text-3xl font-bold text-white">
                Bienvenido
              </h2>
              <p className="text-white/60 text-sm mt-1">
                Ingresa tus credenciales para continuar
              </p>
            </div>
          </div>

          {/* Banner de sesión expirada */}
          {sessionExpired && (
            <div className="rounded-lg px-4 py-3 text-sm font-medium text-yellow-900 bg-yellow-100 border border-yellow-300">
              {logoutReason === 'deploy'
                ? 'El sistema fue actualizado. Inicia sesión nuevamente.'
                : 'Tu sesión fue cerrada por inactividad. Inicia sesión nuevamente.'}
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleLogin} className="space-y-4">

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="login-email" className="text-white/70 text-sm font-medium block">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
                <input
                  id="login-email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={loginData.email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  required
                  data-testid="login-email"
                  className="login-input w-full pl-10 pr-4 py-3 rounded-lg text-white outline-none transition-all text-sm"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(100,160,255,0.2)',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(100,160,255,0.6)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)';
                  }}
                />
              </div>
            </div>

            {/* Contraseña */}
            <div className="space-y-1.5">
              <label htmlFor="login-password" className="text-white/70 text-sm font-medium block">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  data-testid="login-password"
                  className="login-input w-full pl-10 pr-11 py-3 rounded-lg text-white outline-none transition-all text-sm"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(100,160,255,0.2)',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(100,160,255,0.6)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(100,160,255,0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Checkboxes — aceptación ligada al usuario en BD */}
            <div className="space-y-2.5 pt-1">
              <div className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptTerms}
                  onChange={(e) => { setAcceptTerms(e.target.checked); setAutoAccepted(false); }}
                  disabled={autoAccepted}
                  className="mt-0.5 h-4 w-4 accent-blue-400 disabled:opacity-60"
                  style={{ cursor: autoAccepted ? 'default' : 'pointer' }}
                />
                <label
                  htmlFor="accept-terms"
                  className="text-white/70 text-sm leading-snug"
                  style={{ cursor: autoAccepted ? 'default' : 'pointer' }}
                >
                  Acepto los{' '}
                  <span
                    className="text-blue-400 underline underline-offset-2 cursor-pointer hover:text-blue-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      openLegal('Términos y Condiciones', '/Legal/terminos-condiciones.md');
                    }}
                  >
                    términos y condiciones
                  </span>
                </label>
              </div>
              <div className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="accept-privacy"
                  checked={acceptPrivacy}
                  onChange={(e) => { setAcceptPrivacy(e.target.checked); setAutoAccepted(false); }}
                  disabled={autoAccepted}
                  className="mt-0.5 h-4 w-4 accent-blue-400 disabled:opacity-60"
                  style={{ cursor: autoAccepted ? 'default' : 'pointer' }}
                />
                <label
                  htmlFor="accept-privacy"
                  className="text-white/70 text-sm leading-snug"
                  style={{ cursor: autoAccepted ? 'default' : 'pointer' }}
                >
                  Acepto el{' '}
                  <span
                    className="text-blue-400 underline underline-offset-2 cursor-pointer hover:text-blue-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      openLegal('Aviso de Privacidad', '/Legal/aviso-privacidad.md');
                    }}
                  >
                    aviso de privacidad
                  </span>
                </label>
              </div>
            </div>

            {/* Botón Iniciar Sesión */}
            <button
              type="submit"
              disabled={loading || !acceptTerms || !acceptPrivacy}
              data-testid="login-submit"
              className="w-full py-3 rounded-lg font-semibold text-white text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #1d4ed8, #0ea5e9, #10b981)',
                boxShadow: '0 4px 20px rgba(16,185,129,0.25)',
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.opacity = '0.9';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Iniciando…
                </span>
              ) : (
                'Iniciar Sesión →'
              )}
            </button>
          </form>

          {/* Frase de exclusividad */}
          <p className="text-white/40 text-xs text-center leading-relaxed">
            Acceso exclusivo para agentes con clave activa en Estrategias
            Patrimoniales —&nbsp;Allianz · Insignia Life · Quálitas
          </p>
        </div>

        {/* Copyright al fondo */}
        <p className="text-white/25 text-xs text-center pt-8">
          © 2026 EstraPat Estrategias Patrimoniales. Todos los derechos reservados.
        </p>
      </div>

      {/* Columna derecha — vacía; el fondo se ve aquí en desktop */}
      <div className="hidden lg:block lg:flex-1" />
    </div>
  );
};
